/**
 * Dashboard screen — the operator's daily home (FR-10.1, FR-10.4).
 *
 * A rich, fully-dynamic overview, every figure fetched live from the real
 * analytics API (no static data):
 *   - a personalized time-of-day greeting + today's date;
 *   - a live, rule-based AI-insight banner (evidence-before-assertion, §08);
 *   - KPI cards (revenue, occupancy, ADR, RevPAR) with period-over-period
 *     deltas, computed for the CURRENT month vs the PREVIOUS month so the
 *     numbers and deltas are meaningful;
 *   - a year-over-year revenue line chart and a channel-mix donut (Recharts);
 *   - upcoming check-ins and per-property performance.
 *
 * The single gold element (brand §07) is the headline Revenue KPI card.
 *
 * SSR-safe: the initial render is a pure loading state; all network calls and
 * any `Date` reads run inside effects, so the server render matches hydration.
 */
import { useEffect, useState, type ReactElement } from "react";
import { isModuleAvailable } from "../moduleAccess.js";
import { useMe, type MeUser } from "../useMe.js";
import { moduleForAppPath } from "../../modules/registry.js";
import {
  apiDashboardMetrics,
  apiRevenueTrend,
  apiChannelMix,
  apiUpcomingCheckIns,
  apiPropertyPerformance,
  apiDashboardInsight,
  asError,
  formatMoney,
  type DashboardMetrics,
  type RevenueTrendPoint,
  type ChannelMixRow,
  type UpcomingCheckIn,
  type PropertyPerformanceRow,
  type DashboardInsight,
} from "../services.js";
import { RevenueTrendChart } from "../charts/RevenueTrendChart.js";
import { ChannelMixDonut } from "../charts/ChannelMixDonut.js";
import { MoneyValue } from "../components/MoneyValue.js";

/**
 * Derive a safe first name for the greeting from the authenticated user (NO
 * hardcoded/demo name). Fallback chain: firstName → first token of a
 * "First Last" display name (built from first/last) → the email local-part
 * (title-cased) → a neutral "there". Pure + exported for unit testing.
 */
export function greetingFirstName(user: MeUser | null): string {
  if (user) {
    const fn = (user.firstName ?? "").trim();
    if (fn !== "") return fn.split(/\s+/)[0] ?? fn;
    const full = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
    if (full !== "") return full.split(/\s+/)[0] ?? full;
    const email = (user.email ?? "").trim();
    if (email !== "") {
      const local = email.split("@")[0] ?? "";
      const token = local.split(/[._-]/)[0] ?? local;
      if (token !== "") return token.charAt(0).toUpperCase() + token.slice(1);
    }
  }
  return "there";
}

/** A month window `[from, to)` as YYYY-MM-DD, for a month offset from now. */
function monthWindow(offsetMonths: number): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + offsetMonths, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + offsetMonths + 1, 1);
  const iso = (d: Date): string => d.toISOString().slice(0, 10);
  return { from: iso(start), to: iso(end) };
}

/** Time-of-day greeting from a local hour. */
function greeting(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

// ===========================================================================
// Delta indicator
// ===========================================================================

function DeltaIndicator({ pct }: { readonly pct: number | null }): ReactElement | null {
  if (pct === null || !Number.isFinite(pct)) {
    return null;
  }
  const percent = pct * 100;
  const rounded = Math.abs(percent).toFixed(1);
  if (percent > 0) {
    return (
      <span className="analytics-delta analytics-delta--up">
        <span aria-hidden="true">▲</span>
        <span>{rounded}% vs last month</span>
      </span>
    );
  }
  if (percent < 0) {
    return (
      <span className="analytics-delta analytics-delta--down">
        <span aria-hidden="true">▼</span>
        <span>{rounded}% vs last month</span>
      </span>
    );
  }
  return (
    <span className="analytics-delta analytics-delta--flat">
      <span aria-hidden="true">→</span>
      <span>no change</span>
    </span>
  );
}

// ===========================================================================
// KPI cards
// ===========================================================================

function KpiCard({
  label,
  value,
  moneyMinor,
  currency,
  hint,
  gold = false,
  delta,
}: {
  readonly label: string;
  /** A pre-formatted non-money value (e.g. occupancy %). */
  readonly value?: string;
  /** For MONETARY KPIs: the amount in minor units + its currency. */
  readonly moneyMinor?: number;
  readonly currency?: string;
  readonly hint?: string;
  readonly gold?: boolean;
  readonly delta?: ReactElement | null;
}): ReactElement {
  return (
    <div className={gold ? "console-card console-card--gold" : "console-card"}>
      <p className="console-card__label">{label}</p>
      {moneyMinor !== undefined && currency !== undefined ? (
        <MoneyValue minor={moneyMinor} currency={currency} />
      ) : (
        <p className="console-card__value">{value}</p>
      )}
      {delta !== undefined && delta !== null ? delta : null}
      {hint !== undefined ? <p className="console-card__hint">{hint}</p> : null}
    </div>
  );
}

function KpiRow({ metrics }: { readonly metrics: DashboardMetrics }): ReactElement {
  const c = metrics.currency;
  return (
    <div className="console-card-grid dashboard-kpis">
      <KpiCard
        gold
        label="Revenue (this month)"
        moneyMinor={Number(metrics.grossRevenueMinor)}
        currency={c}
        delta={<DeltaIndicator pct={metrics.comparison?.grossPctChange ?? null} />}
        hint="confirmed reservations"
      />
      <KpiCard
        label="Occupancy"
        value={`${(metrics.occupancy * 100).toFixed(1)}%`}
        hint="this month"
      />
      <KpiCard
        label="ADR"
        moneyMinor={Number(metrics.adrMinor)}
        currency={c}
        hint="average daily rate"
      />
      <KpiCard
        label="RevPAR"
        moneyMinor={Number(metrics.revparMinor)}
        currency={c}
        delta={<DeltaIndicator pct={metrics.comparison?.revparPctChange ?? null} />}
        hint="revenue per available room"
      />
    </div>
  );
}

// ===========================================================================
// Insight banner
// ===========================================================================

/**
 * Whether a dashboard insight's action target points at an ENABLED module. A
 * disabled module's deep link is hidden so the dashboard never offers a dead
 * action (the server would otherwise redirect it back to the dashboard).
 */
function insightActionAvailable(actionPath: string): boolean {
  const id = moduleForAppPath(actionPath);
  return id === null ? true : isModuleAvailable(id);
}

/**
 * Temporarily hide the dashboard AI insight banner (per request). Set to `true`
 * to restore it — the data fetch (apiDashboardInsight) and InsightBanner
 * component are left fully intact so this is a one-line toggle.
 */
const SHOW_INSIGHT = false;

function InsightBanner({
  insight,
  onNavigate,
}: {
  readonly insight: DashboardInsight;
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  return (
    <section
      className={`dashboard-insight dashboard-insight--${insight.tone}`}
      aria-label="Insight"
    >
      <span className="dashboard-insight__mark" aria-hidden="true">
        AI
      </span>
      <div className="dashboard-insight__body">
        <p className="dashboard-insight__headline">{insight.headline}</p>
        <p className="dashboard-insight__detail">{insight.detail}</p>
      </div>
      {insight.actionPath !== null &&
      insight.actionLabel !== null &&
      insightActionAvailable(insight.actionPath) ? (
        <button
          type="button"
          className="dashboard-insight__action"
          onClick={() => onNavigate(insight.actionPath as string)}
        >
          {insight.actionLabel} →
        </button>
      ) : null}
    </section>
  );
}

// ===========================================================================
// Upcoming check-ins + property performance
// ===========================================================================

/** A short month/day chip like APR / 19 from an ISO date. */
function dateChip(iso: string): { month: string; day: string } {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) {
    return { month: "", day: iso };
  }
  return {
    month: d.toLocaleString("en-IN", { month: "short" }).toUpperCase(),
    day: String(d.getDate()).padStart(2, "0"),
  };
}

function UpcomingPanel({
  rows,
}: {
  readonly rows: readonly UpcomingCheckIn[];
}): ReactElement {
  return (
    <section className="console-panel dashboard-half" aria-label="Upcoming check-ins">
      <h2 className="console-panel__title">Upcoming check-ins</h2>
      {rows.length > 0 ? (
        <ul className="dashboard-checkins">
          {rows.map((r) => {
            const chip = dateChip(r.checkIn);
            return (
              <li key={r.reservationId} className="dashboard-checkin">
                <span className="dashboard-checkin__date" aria-hidden="true">
                  <span className="dashboard-checkin__month">{chip.month}</span>
                  <span className="dashboard-checkin__day">{chip.day}</span>
                </span>
                <span className="dashboard-checkin__body">
                  <span className="dashboard-checkin__guest">
                    {r.guestName ?? "Guest"}
                  </span>
                  <span className="dashboard-checkin__meta">
                    {r.propertyName ?? "—"}
                    {r.unitName !== null ? ` · ${r.unitName}` : ""}
                  </span>
                </span>
                <span className="dashboard-checkin__right">
                  <span className="dashboard-checkin__channel">{r.channel}</span>
                  <span className="dashboard-checkin__amount">
                    {formatMoney(Number(r.amountMinor), r.currency)}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="console-empty">No upcoming check-ins.</p>
      )}
    </section>
  );
}

function PropertiesPanel({
  rows,
}: {
  readonly rows: readonly PropertyPerformanceRow[];
}): ReactElement {
  const max = rows.reduce(
    (m, r) => (Number(r.revenueMinor) > m ? Number(r.revenueMinor) : m),
    0,
  );
  return (
    <section className="console-panel dashboard-half" aria-label="Property performance">
      <h2 className="console-panel__title">Properties</h2>
      {rows.length > 0 ? (
        <ul className="dashboard-props">
          {rows.map((r) => {
            const rev = Number(r.revenueMinor);
            const width = max > 0 ? `${(rev / max) * 100}%` : "0%";
            return (
              <li key={r.propertyId} className="dashboard-prop">
                <span className="dashboard-prop__head">
                  <span className="dashboard-prop__name">{r.propertyName}</span>
                  <span className="dashboard-prop__rev">
                    {formatMoney(rev, r.currency)}
                  </span>
                </span>
                <span className="dashboard-prop__meta">
                  {r.city ?? "—"} · {r.unitCount} unit
                  {r.unitCount === 1 ? "" : "s"} ·{" "}
                  {(r.occupancy * 100).toFixed(0)}% occ
                </span>
                <span className="dashboard-prop__track" aria-hidden="true">
                  <span className="dashboard-prop__bar" style={{ width }} />
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="console-empty">No properties yet.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

interface DashboardData {
  readonly metrics: DashboardMetrics;
  readonly trend: readonly RevenueTrendPoint[];
  readonly mix: readonly ChannelMixRow[];
  readonly upcoming: readonly UpcomingCheckIn[];
  readonly properties: readonly PropertyPerformanceRow[];
  readonly insight: DashboardInsight | null;
}

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly data: DashboardData }
  | { readonly kind: "error" };

/** Load everything the dashboard needs in parallel (metrics required). */
async function loadDashboard(): Promise<DashboardData> {
  const current = monthWindow(0);
  const previous = monthWindow(-1);
  const [metricsRes, trend, mix, upcoming, properties, insightRes] =
    await Promise.all([
      apiDashboardMetrics({
        from: current.from,
        to: current.to,
        prevFrom: previous.from,
        prevTo: previous.to,
      }),
      apiRevenueTrend(),
      apiChannelMix(),
      apiUpcomingCheckIns(6),
      apiPropertyPerformance(),
      apiDashboardInsight(),
    ]);
  const err = asError(metricsRes);
  if (err !== null) {
    throw new Error(err.error.message);
  }
  const insight = asError(insightRes) === null ? (insightRes as DashboardInsight) : null;
  return {
    metrics: metricsRes as DashboardMetrics,
    trend,
    mix,
    upcoming,
    properties,
    insight,
  };
}

/** Optional navigate hook (passed by the shell via a global event). */
function navigateTo(path: string): void {
  if (typeof window !== "undefined") {
    window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
}

export default function DashboardScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  /** Greeting is client-only (depends on the clock); null on the server. */
  const [now, setNow] = useState<Date | null>(null);
  // Authenticated user for the greeting (trusted session; never a demo name).
  const me = useMe();

  useEffect(() => {
    setNow(new Date());
    let active = true;
    loadDashboard()
      .then((data) => {
        if (active) {
          setState({ kind: "ready", data });
        }
      })
      .catch(() => {
        if (active) {
          setState({ kind: "error" });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const dateLabel =
    now === null
      ? ""
      : now.toLocaleDateString("en-IN", {
          weekday: "long",
          day: "numeric",
          month: "long",
        });
  const greetLabel =
    now === null
      ? "Welcome back"
      : `${greeting(now.getHours())}, ${greetingFirstName(me.user)}`;

  return (
    <section aria-labelledby="dashboard-heading" data-console-screen="dashboard">
      <p className="console-page-eyebrow">{dateLabel || "Portfolio · Live"}</p>
      <h1 id="dashboard-heading">{greetLabel}</h1>

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading your portfolio…
        </p>
      ) : null}

      {state.kind === "error" ? (
        <p className="console-empty" role="status">
          Metrics are unavailable right now. Try refreshing in a moment.
        </p>
      ) : null}

      {state.kind === "ready" ? (
        <>
          {/* AI insight banner hidden for now (per request). The data fetch +
              InsightBanner component are kept intact; flip SHOW_INSIGHT to true
              to restore. */}
          {SHOW_INSIGHT && state.data.insight !== null ? (
            <InsightBanner insight={state.data.insight} onNavigate={navigateTo} />
          ) : null}

          <KpiRow metrics={state.data.metrics} />

          <div className="dashboard-charts">
            <section className="console-panel dashboard-chart-main" aria-label="Revenue trend">
              <h2 className="console-panel__title">Revenue — this year vs last</h2>
              <RevenueTrendChart points={state.data.trend} />
            </section>
            <section className="console-panel dashboard-chart-side" aria-label="Channel mix">
              <h2 className="console-panel__title">Channel mix</h2>
              <ChannelMixDonut rows={state.data.mix} />
            </section>
          </div>

          <div className="dashboard-columns">
            <UpcomingPanel rows={state.data.upcoming} />
            <PropertiesPanel rows={state.data.properties} />
          </div>
        </>
      ) : null}
    </section>
  );
}
